import { useMemo } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useDictionary } from "hooks/use-dictionary";
import { useAuth } from "providers/auth";
import { useChangePasswordAuthenticated } from "hooks/api/common/change-password-authenticated/use-change-password-authenticated";
import { isSuccessStatus } from "utils/is-success-status";
import FormPassword from "components/form/form-password";
import { ImpersonationAwareForm } from "components/impersonation-aware-form";
import { confirmPasswordSchema, passwordSchema } from "utils/validation/password-schema";
import { ROUTES } from "constants/routes";
import apiClient from "api/api-client";
import { setCsrfToken } from "utils/csrf-token";

export const PasswordExpiryModal = () => {
  const dict = useDictionary();
  const { isPasswordChangeRequired, getMe, logout } = useAuth();
  const { mutate, isPending } = useChangePasswordAuthenticated();

  const [schema, resolver] = useMemo(() => {
    const s = yup.object().shape({
      currentPassword: yup.string().required(dict.form.requiredError),
      password: passwordSchema(dict.form),
      confirmPassword: confirmPasswordSchema(dict.form, "password"),
    });
    return [s, yupResolver(s)];
  }, [dict]);

  type FormSchema = yup.InferType<typeof schema>;

  const { control, handleSubmit, setError } = useForm<FormSchema>({ resolver });

  const onSubmit = (data: FormSchema) => {
    mutate(
      { currentPassword: data.currentPassword, password: data.password },
      {
        onSettled: async (response) => {
          if (response?.status && isSuccessStatus(response.status)) {
            await getMe();
          } else if (response?.status === 400) {
            setError("currentPassword", { message: dict.form.invalidCurrentPassword });
          } else if (response?.status === 422) {
            setError("password", { message: dict.form.samePasswordError });
          }
        },
      }
    );
  };

  const handleForgotPassword = async () => {
    await apiClient.post("/auth/logout");
    setCsrfToken(null);
    window.location.href = `${ROUTES.login.home}${ROUTES.login.forgotPassword}`;
  };

  return (
    <Dialog open={isPasswordChangeRequired} disableEscapeKeyDown maxWidth="sm" fullWidth>
      <DialogTitle>{dict.passwordExpiry.title}</DialogTitle>
      <DialogContent className="border-y border-gray-300">
        <div className="pt-6 pb-5 space-y-6">
          <div>{dict.passwordExpiry.description}</div>
          <ImpersonationAwareForm onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-5">
              <FormPassword
                control={control}
                name="currentPassword"
                label={dict.passwordExpiry.currentPassword}
                className="w-full"
              />
              <FormPassword
                control={control}
                name="password"
                label={dict.passwordExpiry.newPassword}
                className="w-full"
              />
              <FormPassword
                control={control}
                name="confirmPassword"
                label={dict.passwordExpiry.confirmPassword}
                className="w-full"
              />
              <DialogActions className="!px-0 flex-col !items-stretch gap-3">
                <Button type="submit" variant="contained" color="primary" disabled={isPending} fullWidth>
                  {isPending ? dict.common.loading : dict.passwordExpiry.submit}
                </Button>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-3">
                  <Button variant="text" color="primary" onClick={handleForgotPassword} fullWidth>
                    {dict.login.forgotPassword.link}
                  </Button>
                  <Button variant="outlined" color="primary" onClick={logout} fullWidth>
                    {dict.header.logout}
                  </Button>
                </div>
              </DialogActions>
            </div>
          </ImpersonationAwareForm>
        </div>
      </DialogContent>
    </Dialog>
  );
};
